/**
 * Generic City Layout Wrapper
 *
 * Provides consistent header, footer, and navigation for all city landing pages.
 * Fully parameterized for any city.
 *
 * Usage:
 *   <CityLayout
 *     cityName="Houston"
 *     stateName="Texas"
 *     logoSrc="/media_houston/houston-logo-main.png"
 *     navLinks={houstonNavLinks}
 *     footerLinks={houstonFooterLinks}
 *   >
 *     <Outlet />
 *   </CityLayout>
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { Facebook, Instagram } from '@/components/shared/brand-icons';
import { WaitlistTypeDialog } from './WaitlistTypeDialog';
import { cn } from '@/lib/shared/utils/utils';

export interface NavLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface SocialLink {
  platform: 'facebook' | 'twitter' | 'instagram' | 'linkedin';
  href: string;
  label?: string;
}

export interface CityLayoutProps {
  cityName: string;
  stateName?: string;
  logoSrc?: string;
  logoAlt?: string;
  navLinks?: NavLink[];
  footerLinks?: FooterLink[];
  socialLinks?: SocialLink[]; // Social media links to display in footer
  contactEmail?: string;
  primaryCtaText?: string;
  primaryCtaHref?: string;
  onPrimaryCtaClick?: () => void;
  enableWaitlistDialog?: boolean; // Enable dual waitlist dialog (Resident/Vendor)
  vendorsPagePath?: string; // Path to vendors page (e.g., '/vendors')
  children: React.ReactNode;
}

/**
 * CityLayout - Parameterized layout wrapper for city landing pages
 *
 * Provides header with logo, navigation, and CTA button.
 * Provides footer with about, contact, and legal links.
 *
 * @example
 * ```tsx
 * <CityLayout
 *   cityName="Houston"
 *   stateName="Texas"
 *   logoSrc="/media_houston/houston-logo-main.png"
 *   navLinks={[
 *     { label: 'Features', href: '/#features' },
 *     { label: 'Pricing', href: '/#pricing' },
 *     { label: 'Vendors', href: '/vendors' }
 *   ]}
 *   footerLinks={[
 *     { label: 'Terms of Service', href: '/terms' },
 *     { label: 'Privacy Policy', href: '/privacy' }
 *   ]}
 *   contactEmail="hello@toolboxlogic.com"
 *   primaryCtaText="Join Waitlist"
 *   primaryCtaHref="/#interest-form"
 * >
 *   {children}
 * </CityLayout>
 * ```
 */
export function CityLayout({
  cityName,
  stateName = '',
  logoSrc = '/logo.png',
  logoAlt,
  navLinks = [],
  footerLinks = [],
  socialLinks = [],
  contactEmail = '' /* 'hello@toolboxlogic.com' */,
  primaryCtaText = 'Join Waitlist',
  primaryCtaHref = '/#interest-form',
  onPrimaryCtaClick,
  enableWaitlistDialog = false,
  vendorsPagePath = '/vendors',
  children,
}: CityLayoutProps) {
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [waitlistDialogOpen, setWaitlistDialogOpen] = React.useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = React.useState(true);
  const lastScrollY = React.useRef(0);
  const location = useLocation();
  const navigate = useNavigate();

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;
  const computedLogoAlt = logoAlt || `${cityName} logo`;

  React.useEffect(() => {
    let ticking = false;
    const totalHeightRef = { current: document.documentElement.scrollHeight - window.innerHeight };

    const recalcTotalHeight = () => {
      totalHeightRef.current = document.documentElement.scrollHeight - window.innerHeight;
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const progress = (currentScrollY / totalHeightRef.current) * 100;
          setScrollProgress(progress);

          // Smart sticky header logic
          // Hide on scroll down, show on scroll up
          // Always show if near top or if mobile menu is open
          if (currentScrollY < lastScrollY.current || currentScrollY < 50 || mobileMenuOpen) {
            setIsHeaderVisible(true);
          } else if (currentScrollY > 100 && currentScrollY > lastScrollY.current) {
            setIsHeaderVisible(false);
          }

          lastScrollY.current = currentScrollY;
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', recalcTotalHeight);
    recalcTotalHeight();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', recalcTotalHeight);
    };
  }, [mobileMenuOpen]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      scrollToTop();
    } else {
      window.location.href = '/';
    }
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Handle hash navigation on same page
    if (href.startsWith('/#') && location.pathname === '/') {
      e.preventDefault();
      const sectionId = href.substring(2);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleCtaClick = () => {
    if (enableWaitlistDialog) {
      setWaitlistDialogOpen(true);
    } else if (onPrimaryCtaClick) {
      onPrimaryCtaClick();
    } else if (primaryCtaHref.startsWith('/#')) {
      const sectionId = primaryCtaHref.substring(2);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const scrollToForm = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const formElement = document.getElementById('interest-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const formElement = document.getElementById('interest-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <header
        className={cn(
          'fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-transform duration-300',
          !isHeaderVisible && !mobileMenuOpen ? '-translate-y-full' : 'translate-y-0',
        )}
      >
        {/* Scroll Progress Bar */}
        <div className="absolute top-0 left-0 h-0.5 w-full overflow-hidden">
          <div
            className="h-full bg-primary origin-left transition-transform duration-75 ease-out"
            style={{ transform: `scaleX(${scrollProgress / 100})` }}
          />
        </div>
        <div className="container flex h-16 items-center justify-between px-4 sm:px-6">
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer flex-shrink-0 min-w-0"
          >
            <img
              src={logoSrc}
              alt={computedLogoAlt}
              className="h-10 w-10 sm:h-11 sm:w-11 flex-shrink-0"
              loading="eager"
              width={44}
              height={44}
              decoding="async"
            />
            <div className="flex flex-col justify-center">
              <span
                className="text-base sm:text-lg lg:text-xl font-semibold text-primary leading-none whitespace-nowrap truncate"
                style={{
                  fontFamily: 'Baskerville, Baskerville Old Face, "Libre Baskerville", serif',
                }}
              >
                {cityName}
              </span>
              <span className="text-[10px] sm:text-xs font-medium text-muted-foreground leading-tight uppercase tracking-wide">
                Community App
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 lg:gap-6">
            {navLinks.map((link) => {
              if (link.external || link.href.startsWith('http')) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="text-sm font-medium hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                );
              }

              // Internal link (hash or route)
              if (link.href.startsWith('/#')) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm font-medium hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                );
              }

              // React Router link
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button - only on small screens, right-aligned */}
          {navLinks.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden min-h-[44px] px-2 ml-auto mr-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          )}

          {/* Primary CTA - visible on all breakpoints */}
          {primaryCtaHref.startsWith('/#') ? (
            <Button
              onClick={handleCtaClick}
              size="sm"
              variant="default"
              className="shadow-sm text-xs sm:text-sm px-3 sm:px-4 whitespace-nowrap flex-shrink-0"
            >
              {primaryCtaText}
            </Button>
          ) : (
            <Button
              asChild
              size="sm"
              variant="default"
              className="shadow-sm text-xs sm:text-sm px-3 sm:px-4 whitespace-nowrap flex-shrink-0"
            >
              <Link to={primaryCtaHref}>{primaryCtaText}</Link>
            </Button>
          )}
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 z-[60] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <nav className="container px-4 py-6 flex flex-col gap-4">
            {navLinks.map((link) => {
              const commonClasses =
                'text-lg font-medium hover:text-primary transition-colors py-3 min-h-[48px] flex items-center border-b';
              if (link.external || link.href.startsWith('http')) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className={commonClasses}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                );
              }
              if (link.href.startsWith('/#')) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      handleNavClick(e, link.href);
                      setMobileMenuOpen(false);
                    }}
                    className={commonClasses}
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={commonClasses}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-20">{children}</main>

      {/* Waitlist Type Selection Dialog */}
      {enableWaitlistDialog && (
        <WaitlistTypeDialog
          open={waitlistDialogOpen}
          onOpenChange={setWaitlistDialogOpen}
          cityName={cityName}
          onSelectResident={() => {
            setWaitlistDialogOpen(false);
            // Small delay to allow dialog to close and body scroll lock to release
            setTimeout(() => {
              scrollToForm();
            }, 100);
          }}
          onSelectVendor={() => {
            setWaitlistDialogOpen(false);
            navigate(vendorsPagePath, { state: { openForm: true } });
          }}
        />
      )}

      {/* Footer */}
      <footer className="border-t bg-muted/50 py-8 sm:py-12">
        <div className="container px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <h3 className="font-semibold mb-3 text-sm sm:text-base">About</h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                ToolboxLogic provides community organizing tools for {cityStateDisplay} residents
                who want to amplify their voice and build collective power.
              </p>
            </div>
            <div>
              {/* Social Links - clean inline placement */}
              {socialLinks.length > 0 && (
                <>
                  <h3 className="font-semibold mb-3 text-sm sm:text-base">Community</h3>
                  <div className="flex flex-col gap-2 text-xs sm:text-sm text-muted-foreground">
                    {socialLinks.map((link) => {
                      const getSocialIcon = (platform: string) => {
                        switch (platform) {
                          case 'facebook':
                            return <Facebook className="h-4 w-4" />;
                          case 'instagram':
                            return <Instagram className="h-4 w-4" />;
                          default:
                            return null;
                        }
                      };

                      return (
                        <a
                          key={link.href}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 hover:text-primary transition-colors min-h-[44px]"
                          aria-label={link.label || link.platform}
                        >
                          {getSocialIcon(link.platform)}
                          <span>{link.label || link.platform}</span>
                        </a>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="sm:col-span-2 md:col-span-1">
              <h3 className="font-semibold mb-3 text-sm sm:text-base">Legal</h3>
              <div className="flex flex-col gap-2 text-xs sm:text-sm text-muted-foreground">
                {footerLinks.map((link) => {
                  if (link.external || link.href.startsWith('http')) {
                    return (
                      <a
                        key={link.href}
                        href={link.href}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className="hover:text-primary transition-colors min-h-[44px] flex items-center"
                      >
                        {link.label}
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="hover:text-primary transition-colors min-h-[44px] flex items-center"
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t text-center text-xs sm:text-sm text-muted-foreground">
            © {new Date().getFullYear()} ToolboxLogic. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
